import React, { useEffect, useState } from 'react';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import { translate } from 'components/Translation';
import { Switch, Route } from 'react-router-dom';
import UsageDetails from './UsageDetails';
import SingleUsageDetails from './SingleUsageDetails';
import { decode } from 'utils/crypto/crypto';
import { Usage } from 'utils/api/models/numbers/usage';
import { NumbersService } from 'services/numbers/numbers';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { useDispatch, useSelector } from 'react-redux';
import { finalize } from 'rxjs/operators';
import { TagManager } from 'commons/gtm/tagManager';
import { getLanguage } from 'utils/language/language';
import { upIdSelector, userIdSelector } from 'redux/modules/session/selectors';

interface IUsagePageProps {
	match: {
		path: string;
		url: string;
		params: {
			phoneNumberEncoded: string;
			accountType: string;
			billingType: string;
			upAccountId: string;
			accountNumber: string;
		};
	};
	accountName: string;
}

const UsagePage = ({ match, accountName }: IUsagePageProps) => {
	const { phoneNumberEncoded, accountType, billingType } = match.params;
	const [bundles, setBundles] = useState<Usage | null>(null);
	const [loading, setLoading] = useState<boolean>(true);
	const phoneNumber = decode(phoneNumberEncoded);
	const dispatch = useDispatch();

	const accId = useSelector(upIdSelector);
	const userId = useSelector(userIdSelector);

	useEffect(() => {
		setLoading(true);
		dispatch(showLoadingIndicator());
		NumbersService.getUsage({
			phoneNumber,
			billingType,
			accountType,
		})
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator());
					setLoading(false);
				})
			)
			.subscribe(
				(data) => {
					setBundles(data);
				},
				() => {
					dispatch(hideLoadingIndicator());
					setLoading(false);
				}
			);
		return () => {
			dispatch(hideLoadingIndicator());
		};
	}, [phoneNumber, billingType, accountType, dispatch]);

	const filterType = (type: string) => ({
		totals: bundles?.totals.filter((total) => type === total.type),
		packages: bundles?.packages.filter((bundle) => type === bundle.type),
	});

	React.useEffect(() => {
		TagManager.dataLayer({
			dataLayer: {
				event: 'My Services',
				step: `My Numbers: Usage`,
				accountID: accId,
				billingID: match.params.accountNumber,
				userId: userId,
				language: getLanguage(),
			},
		});
	}, []);
	return (
		<div>
			<BreadcrumbsItem to={match.url}>{`${phoneNumber} (${accountName}) - ${translate(
				'path.usage'
			)}`}</BreadcrumbsItem>
			<ConditionalRender
				show={!loading && !!bundles}
				onTrue={() => (
					<Switch>
						<Route
							path={`${match.path}/details/:type(data|voice|sms)`}
							render={(props: any) => (
								<SingleUsageDetails
									bundles={filterType(props.match.params.type.toUpperCase())}
									phoneNumber={phoneNumber}
									{...props}
								/>
							)}
						/>
						<Route
							path={match.path}
							exact
							render={(props: any) => (
								<UsageDetails totals={bundles?.totals || []} balance={bundles} {...props} />
							)}
						/>
					</Switch>
				)}
			/>
		</div>
	);
};

export default UsagePage;
